import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import {
  usePilgrimNationalityStatsReport,
  usePilgrimNationalityStatsReportMutation,
} from "@/services/reports";
import { cn, filterObject } from "@/utils/helpers";
import { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";

function cellInt(row, key) {
  const v = row?.[key];
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cellIntFirstKey(row, keys) {
  for (const key of keys) {
    const v = row?.[key];
    if (v != null && v !== "") return cellInt(row, key);
  }
  return 0;
}

export const PilgrimNationalityStatsReport = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
  } = usePilgrimNationalityStatsReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      orientation: "landscape",
      per_page: 15,
    },
    disabled: isGeneratingReport,
  });

  const format = form.watch("format");
  const nationalityId = form.watch("nationality_id");
  const perPage = form.watch("per_page");
  const isJson = format === "json";

  const jsonQueryParams = useMemo(
    () =>
      filterObject({
        format: "json",
        page,
        nationality_id: nationalityId,
        per_page: perPage,
      }),
    [page, nationalityId, perPage]
  );

  const {
    data: reportRes,
    isLoading: isLoadingReport,
    refetch,
    isRefetching: isRefetchingReport,
  } = usePilgrimNationalityStatsReport({
    params: jsonQueryParams,
    enabled: isJson,
  });

  const rows = reportRes?.data?.data || [];
  const meta = reportRes?.data?.meta || {};

  const {
    control,
    formState: { errors },
    getValues,
  } = form;

  const formText = {
    json: { buttonText: t("VIEW_REPORTS") },
    pdf: { buttonText: t("EXPORT_PDF") },
    excel: { buttonText: t("EXPORT_EXCEL") },
  };

  const formContent = formText[format] || formText.json;

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="pilgrim-nationality-stats-format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    !isJson && {
      labelProps: { children: t("ORIENTATION") },
      controllerProps: {
        name: "orientation",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="pilgrim-nationality-stats-orientation"
            placeholder={t("ORIENTATION")}
            status={error ? "error" : undefined}
            options={[
              { value: "portrait", label: t("PORTRAIT") },
              { value: "landscape", label: t("LANDSCAPE") },
            ]}
          />
        ),
      },
    },
    isJson && {
      labelProps: { children: t("PER_PAGE") },
      controllerProps: {
        name: "per_page",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="pilgrim-nationality-stats-per_page"
            placeholder={t("PER_PAGE")}
            status={error ? "error" : undefined}
            options={[
              { value: 10, label: "10" },
              { value: 15, label: "15" },
              { value: 20, label: "20" },
              { value: 50, label: "50" },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("NATIONALITY") },
      controllerProps: {
        name: "nationality_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            allowClear
            id="pilgrim-nationality-stats-nationality_id"
            placeholder={t("NATIONALITY")}
            status={error ? "error" : undefined}
            optionKey="nationalities"
            options={(n) => ({
              value: n.id,
              label: <SelectCountry.Display value={n.id} />,
            })}
          />
        ),
      },
    },
  ].filter(Boolean);

  const pageSize = meta?.per_page || Number(getValues("per_page")) || 15;

  const columns = useMemo(
    () => [
      {
        name: t("NATIONALITY_STATS_COL_SERIAL"),
        key: "serial",
        filterable: false,
        render: (_, row, index) =>
          row?.serial !== undefined && row?.serial !== null && row.serial !== ""
            ? row.serial
            : (page - 1) * pageSize + index + 1,
      },
      {
        name: t("NATIONALITY"),
        key: "nationality_id",
        filterable: false,
        // render: (_, row) => row?.nationality ?? "—",
        render: (value) => <SelectCountry.Display value={value} />,
      },
      {
        name: t("NATIONALITY_STATS_ARRIVALS_TOTAL"),
        key: "arrivals_total",
        filterable: false,
        render: (_, row) => cellInt(row, "arrivals_total"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_JEDDAH_ARR"),
        key: "arrivals_jeddah",
        filterable: false,
        render: (_, row) => cellInt(row, "arrivals_jeddah"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_MADINA_ARR"),
        key: "arrivals_madina",
        filterable: false,
        render: (_, row) =>
          cellIntFirstKey(row, [
            "arrivals_madina",
            "arrivals_medina",
            "arrivals_medinah",
          ]),
      },
      {
        name: t("NATIONALITY_STATS_PORT_TAIF_ARR"),
        key: "arrivals_taif",
        filterable: false,
        render: (_, row) => cellInt(row, "arrivals_taif"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_YANBU_ARR"),
        key: "arrivals_yanbu",
        filterable: false,
        render: (_, row) => cellInt(row, "arrivals_yanbu"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_LAND_ARR"),
        key: "arrivals_land",
        filterable: false,
        render: (_, row) => cellInt(row, "arrivals_land"),
      },
      {
        name: t("NATIONALITY_STATS_INCIDENT_DEATH"),
        key: "incidents_death",
        filterable: false,
        render: (_, row) => cellInt(row, "incidents_death"),
      },
      {
        name: t("NATIONALITY_STATS_INCIDENT_ARREST"),
        key: "incidents_arrest",
        filterable: false,
        render: (_, row) => cellInt(row, "incidents_arrest"),
      },
      {
        name: t("NATIONALITY_STATS_INCIDENT_SICK"),
        key: "incidents_sick",
        filterable: false,
        render: (_, row) => cellInt(row, "incidents_sick"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_JEDDAH_DEP"),
        key: "departures_jeddah",
        filterable: false,
        render: (_, row) => cellInt(row, "departures_jeddah"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_MADINA_DEP"),
        key: "departures_madina",
        filterable: false,
        render: (_, row) =>
          cellIntFirstKey(row, [
            "departures_madina",
            "departures_medina",
            "departures_medinah",
          ]),
      },
      {
        name: t("NATIONALITY_STATS_PORT_TAIF_DEP"),
        key: "departures_taif",
        filterable: false,
        render: (_, row) => cellInt(row, "departures_taif"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_YANBU_DEP"),
        key: "departures_yanbu",
        filterable: false,
        render: (_, row) => cellInt(row, "departures_yanbu"),
      },
      {
        name: t("NATIONALITY_STATS_PORT_LAND_DEP"),
        key: "departures_land",
        filterable: false,
        render: (_, row) => cellInt(row, "departures_land"),
      },
      {
        name: t("NATIONALITY_STATS_DEPARTURES_TOTAL"),
        key: "departures_total",
        filterable: false,
        render: (_, row) => cellInt(row, "departures_total"),
      },
      {
        name: t("NATIONALITY_STATS_REMAINING"),
        key: "remaining",
        filterable: false,
        render: (_, row) => cellInt(row, "remaining"),
      },
    ],
    [t, page, pageSize]
  );

  const onSubmit = (data) => {
    const payload = filterObject({
      ...getValues(),
      page,
    });

    if (data.format === "json") {
      setPage(1);
      return refetch();
    }

    generateReport({ params: payload });
  };

  const backTo =
    window.history.length > 1 ? -1 : `/app/makkah/pilgrim-reports`;

  const tableLoading = isLoadingReport || isRefetchingReport;
  const isLoading = isGeneratingReport || isLoadingReport;

  return (
    <div>
      <PageHeader
        title={t("PILGRIM_NATIONALITY_STATS_REPORT_TITLE")}
        sticky={false}
      />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            controllers={ctrls.map((config) => ({
              ...config,
              labelProps: {
                ...config.labelProps,
                className: cn("fw-bold", config.labelProps.className),
              },
              controllerProps: {
                ...config.controllerProps,
                control,
                errors,
              },
              layout: {
                colProps: { xs: 6, md: 6 },
              },
            }))}
          />
          <ActionsFooter>
            <Button
              disabled={isLoading}
              className="btn btn-primary btn-sm"
              type="submit"
            >
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger btn-sm"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button
              onClick={() => form.reset()}
              disabled={isLoading}
              className="btn btn-outline-primary btn-sm"
              type="button"
            >
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={rows}
            columns={columns}
            loading={tableLoading}
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || pageSize,
              onChange: (newPage) => setPage(newPage),
            }}
          />
        )}
      </Container>
    </div>
  );
};
